import React from 'react';
import { CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { LocationAutocomplete } from './LocationAutocomplete';
import { BreedSelector } from '@/components/BreedSelector';

interface ProfileFormData {
  ranchName: string;
  location: string;
  description: string;
  locationCoordinates: [number, number] | null;
  profileImage: string;
  coverImage: string;
  breeds: string[];
}

interface ProfileFormProps {
  formData: ProfileFormData;
  loading: boolean;
  hasChanges: boolean;
  subscriptionActive: boolean;
  authLoading: boolean;
  onLocationChange: (location: string, coordinates?: [number, number]) => void;
  onInputChange: (field: string, value: string) => void;
  onBreedAdd: (breed: string) => void;
  onBreedRemove: (breed: string) => void;
  onSaveChanges: () => void;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({
  formData,
  loading,
  hasChanges,
  subscriptionActive,
  authLoading,
  onLocationChange,
  onInputChange,
  onBreedAdd,
  onBreedRemove,
  onSaveChanges
}) => {
  const isDisabled = !authLoading && !subscriptionActive;

  return (
    <CardContent className="space-y-6 pt-4">
      {!authLoading && subscriptionActive === false && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700">
            Your subscription is inactive. Subscribe to edit your profile information.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="ranchName">Ranch/Business Name</Label>
          <Input 
            id="ranchName" 
            value={formData.ranchName}
            onChange={(e) => onInputChange('ranchName', e.target.value)}
            disabled={isDisabled}
            className={isDisabled ? "bg-gray-100 text-gray-500" : ""}
          />
        </div>
        <div>
          <LocationAutocomplete
            value={formData.location}
            onChange={onLocationChange}
            placeholder="Enter your location..."
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label>Breeds You Work With</Label>
          <div className="space-y-3">
            <BreedSelector
              value=""
              onValueChange={onBreedAdd}
              placeholder="Select a breed to add..."
              className="w-full"
              disabled={isDisabled}
            />
            {formData.breeds.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.breeds.map((breed) => (
                  <Badge key={breed} variant="secondary" className="flex items-center gap-1">
                    {breed}
                    {!isDisabled && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-transparent"
                        onClick={() => onBreedRemove(breed)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea 
          id="description" 
          value={formData.description}
          onChange={(e) => onInputChange('description', e.target.value)}
          rows={4}
          placeholder="Tell us about your ranch or business..."
          disabled={isDisabled}
          className={isDisabled ? "bg-gray-100 text-gray-500" : ""}
        />
      </div>

      {hasChanges && !isDisabled && (
        <Button 
          className="bg-green-700 hover:bg-green-800"
          onClick={onSaveChanges}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      )}
    </CardContent>
  );
};
